
import React, { useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, Moon, Sun, Bell } from 'lucide-react';
import CurrencySwitcher from './CurrencySwitcher';
import { useTheme } from '../ThemeContext';
import { useNotifications } from '../NotificationContext';
import { useTranslation } from '../i18n/LanguageContext';
import { LanguageCode } from '../../types';

const DesktopHeader: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage } = useTranslation();
  const { notifications, unreadCount, markAllAsRead, clearAll } = useNotifications();
  const [showNotifications, setShowNotifications] = useState(false);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'error': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'success': return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      default: return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  return (
    <header className="hidden md:flex items-center justify-between px-8 py-4 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-30 transition-colors duration-200">
      <div className="flex items-center gap-4">
         <CurrencySwitcher />
         <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-900 px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Lang</span>
            <select 
              value={language} 
              onChange={(e) => setLanguage(e.target.value as LanguageCode)}
              className="bg-transparent border-none text-xs font-black text-slate-900 dark:text-white focus:ring-0 outline-none cursor-pointer"
            >
              <option value="en">EN</option>
              <option value="es">ES</option>
              <option value="de">DE</option>
              <option value="fr">FR</option>
              <option value="hi">HI</option>
            </select>
         </div>
      </div>
      <div className="flex items-center gap-6">
        <button 
          onClick={toggleTheme}
          className="p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:text-slate-400 dark:hover:text-white bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all"
          title={theme === 'light' ? "Switch to Dark Mode" : "Switch to Light Mode"}
        >
          {theme === 'light' ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></svg>
          )}
        </button>
        <div className="h-6 w-px bg-slate-200 dark:bg-slate-700"></div>
        
        <div className="relative">
          <button 
            onClick={() => setShowNotifications(!showNotifications)}
            className={`text-slate-400 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-200 relative p-1 rounded-lg transition-colors ${showNotifications ? 'bg-slate-100 dark:bg-slate-700' : ''}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-slate-800 flex items-center justify-center">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              </span>
            )}
          </button>

          {showNotifications && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowNotifications(false)}></div>
              <div className="absolute right-0 top-full mt-4 w-96 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 z-20 overflow-hidden animate-in fade-in slide-in-from-top-2">
                <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-slate-50/50 dark:bg-slate-900/50">
                  <h3 className="font-bold text-slate-900 dark:text-white text-sm">Notifications</h3>
                  <div className="flex gap-2">
                    <button onClick={markAllAsRead} className="text-[10px] font-black uppercase text-indigo-600 dark:text-indigo-400 hover:underline">Mark all read</button>
                    <span className="text-slate-300 dark:text-slate-600">|</span>
                    <button onClick={clearAll} className="text-[10px] font-black uppercase text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">Clear</button>
                  </div>
                </div>
                <div className="max-h-[400px] overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">
                      <p className="text-xs font-medium">No new notifications.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-700">
                      {notifications.map(notif => (
                        <div key={notif.id} className={`p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors flex gap-3 ${!notif.read ? 'bg-indigo-50/30 dark:bg-indigo-900/10' : ''}`}>
                          <div className="mt-0.5">{getTypeIcon(notif.type)}</div>
                          <div className="flex-1 space-y-1">
                            <p className={`text-xs ${!notif.read ? 'font-bold text-slate-900 dark:text-white' : 'font-medium text-slate-600 dark:text-slate-300'}`}>{notif.title}</p>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-tight">{notif.message}</p>
                            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">{notif.timestamp.toLocaleTimeString()}</p>
                          </div>
                          {!notif.read && <div className="w-2 h-2 rounded-full bg-indigo-500 mt-1.5 shrink-0"></div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-3 pl-6 border-l border-slate-200 dark:border-slate-700">
          <div className="text-right">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">Alex Rivera</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Subscription Manager</p>
          </div>
          <img src="https://picsum.photos/40/40?random=1" className="w-10 h-10 rounded-full border border-slate-200 dark:border-slate-600" alt="Profile" />
        </div>
      </div>
    </header>
  );
};

export default DesktopHeader;
